import * as v from "valibot";

/**
 * Brief / lead schema — shared by the frontend form (app/ui/Hire/) and the
 * Hono server (server/src/index.ts). Both halves validate with the same
 * Valibot schema so the server re-checks everything the client checked
 * (a hostile client can post arbitrary JSON, so the server is the source
 * of truth). The Cloudflare Worker version at workers/briefs/ is preserved
 * as an alternative deploy path and uses the same schema.
 */

export const SERVICE_OPTIONS = [
  { value: "games", label: "Games" },
  { value: "engines", label: "Engines (Unity / Unreal)" },
  { value: "native-android", label: "Native Android" },
  { value: "react-native", label: "React Native" },
  { value: "ar-vr", label: "AR / VR" },
  { value: "ai", label: "AI / LLM features" },
  { value: "fintech", label: "Fintech / automation" },
  { value: "other", label: "Something else" },
] as const;

export type ServiceValue = (typeof SERVICE_OPTIONS)[number]["value"];

export const BUDGET_OPTIONS = [
  { value: "under-5k", label: "Under $5k" },
  { value: "5-15k", label: "$5k – $15k" },
  { value: "15-50k", label: "$15k – $50k" },
  { value: "50-150k", label: "$50k – $150k" },
  { value: "discuss", label: "Let's discuss" },
] as const;

export type BudgetValue = (typeof BUDGET_OPTIONS)[number]["value"];

export const TIMELINE_OPTIONS = [
  { value: "asap", label: "ASAP (this month)" },
  { value: "1-3mo", label: "1 – 3 months" },
  { value: "3-6mo", label: "3 – 6 months" },
  { value: "flexible", label: "Flexible" },
] as const;

export type TimelineValue = (typeof TIMELINE_OPTIONS)[number]["value"];

const serviceValues = SERVICE_OPTIONS.map((o) => o.value) as [
  ServiceValue,
  ...ServiceValue[],
];
const budgetValues = BUDGET_OPTIONS.map((o) => o.value) as [
  BudgetValue,
  ...BudgetValue[],
];
const timelineValues = TIMELINE_OPTIONS.map((o) => o.value) as [
  TimelineValue,
  ...TimelineValue[],
];

export const BriefSchema = v.object({
  name: v.pipe(
    v.string(),
    v.trim(),
    v.minLength(2, "Tell us your name (2+ characters)."),
    v.maxLength(80, "That name is unusually long — please shorten it."),
  ),
  email: v.pipe(
    v.string(),
    v.trim(),
    v.email("That doesn't look like a valid email."),
    v.maxLength(120, "That email is too long."),
  ),
  service: v.picklist(serviceValues, "Pick a service category."),
  budget: v.picklist(budgetValues, "Pick a budget band."),
  timeline: v.picklist(timelineValues, "Pick a timeline."),
  message: v.pipe(
    v.string(),
    v.trim(),
    v.minLength(10, "Give us at least 10 characters — what are you trying to build?"),
    v.maxLength(2000, "Please keep the brief under 2000 characters."),
  ),
  /** Optional cubie id the visitor came from, for analytics. */
  sourceCubie: v.optional(v.string()),
  /** Anti-bot: real users don't fill this hidden input. */
  honeypot: v.optional(v.pipe(v.string(), v.maxLength(0))),
});

export type Brief = v.InferOutput<typeof BriefSchema>;

export const BriefDraftSchema = v.partial(BriefSchema);
export type BriefDraft = v.InferOutput<typeof BriefDraftSchema>;
